import Dexie from 'dexie';
import browser from 'webextension-polyfill';
import { db, DBPage, Operation, renormalizeAll } from '../db/db';
import { closeTab } from '../chrome/chromeActions';
import { addTag, fetchPages, fetchPagesWithNoTags, Page } from '../db/pages';
import { getTagsToProtectFromCleanup } from '../db/prefs';
import { StashTag, SystemTags } from '../db/constants';
import { NormalizedUrl, normalizeUrl } from '../chrome/normalizeUrl';
import { logger } from '../utils/logger';
import { enqueueOperation } from '../db/queue';

const PROCESS_QUEUE_MESSAGE = 'processQueue';
let isProcessing = false;

/** Returns whether processQueue is currently running. Used for diagnostics. */
export function isQueueProcessing(): boolean {
  return isProcessing;
}

// Chrome-specific non-standard performance.memory extension
type PerformanceWithMemory = Performance & { memory?: { usedJSHeapSize: number } };

export async function initQueueProcessor() {
  browser.runtime.onMessage.addListener((message) => {
    try {
      if (message === PROCESS_QUEUE_MESSAGE) {
        logger.info('[QueueWorker] Received processQueue message');
        processQueue();
      }
    } catch (e: unknown) {
      logger.error('[QueueWorker] Error in message listener:', e);
    }
    return undefined;
  });

  try {
    // Reset any operations that were stuck in 'processing' state (e.g. from crash/reload)
    await resetStuckOperations();

    // Prune old completed/failed operations to prevent table bloat
    await cleanupOldOperations();

    // Also check on startup
    processQueue();
  } catch (e: unknown) {
    logger.error('[QueueWorker] Fatal error during initQueueProcessor:', e);
  }
}

async function cleanupOldOperations() {
    try {
        // Delete completed/failed operations older than 7 days to prevent the table
        // from growing unboundedly. A large operations table slows down backup
        // (chunkedExportDB reads every row) and can trigger Dexie call-stack overflows
        // when internal query structures are built over thousands of rows.
        const RETENTION_MS = 7 * 24 * 60 * 60 * 1000;
        const cutoff = Date.now() - RETENTION_MS;
        const oldOpIds = (await db.operations
            .where('createdAt').below(cutoff)
            .filter(op => op.status === 'completed' || op.status === 'failed')
            .primaryKeys()) as number[];

        if (oldOpIds.length === 0) return;

        logger.info(`[QueueWorker] Cleaning up ${oldOpIds.length} old completed/failed operations`);
        const BATCH_SIZE = 100;
        for (let i = 0; i < oldOpIds.length; i += BATCH_SIZE) {
            await db.operations.bulkDelete(oldOpIds.slice(i, i + BATCH_SIZE));
        }
        logger.info('[QueueWorker] Operation cleanup complete');
    } catch (e: unknown) {
        logger.error('[QueueWorker] Error cleaning up old operations:', e);
    }
}

async function resetStuckOperations() {
    try {
        const stuckOps = await db.operations.where('status').equals('processing').toArray();
        if (stuckOps.length > 0) {
            logger.info(`[QueueWorker] Resetting ${stuckOps.length} stuck operations to pending`);
            const ids = stuckOps.map(op => op.id).filter(id => id !== undefined) as number[];
            
            // Batch the IDs into chunks of 100 to prevent Dexie call stack size exceeded errors
            // during internal query parameter expansions in anyOf()
            const batchSize = 100;
            for (let i = 0; i < ids.length; i += batchSize) {
                 const batch = ids.slice(i, i + batchSize);
                 await db.operations.where('id').anyOf(batch).modify({ status: 'pending' });
            }
        }
    } catch (e: unknown) {
        logger.error('[QueueWorker] Error resetting stuck operations:', e);
    }
}

async function processQueue() {
  if (isProcessing) return;
  isProcessing = true;
  logger.debug('[QueueWorker] Started processing queue');
  const startMemory = typeof performance !== 'undefined' ? (performance as PerformanceWithMemory).memory?.usedJSHeapSize : undefined;

  try {
    // Log a breakdown of pending ops by type so we can see what's accumulating.
    // This fires on every processQueue call — kept at debug level to avoid noise
    // except when embedding ops are queued, which is elevated to info.
    try {
      const allPending = await db.operations.where('status').equals('pending').toArray();
      if (allPending.length > 0) {
        const byType: Record<string, number> = {};
        for (const op of allPending) { byType[op.type] = (byType[op.type] || 0) + 1; }
        const inBackoff = isEmbeddingInBackoff();
        const backoffInfo = inBackoff
          ? ` | embedding backoff: ${Math.ceil((embeddingBackoff.nextRetryAt - Date.now()) / 1000)}s remaining (failure #${embeddingBackoff.failureCount})`
          : '';
        const level = (byType['generateEmbedding'] ?? 0) > 0 ? 'info' : 'debug';
        logger[level](`[QueueWorker] Pending ops: ${JSON.stringify(byType)}${backoffInfo}`);
      }
    } catch (_) {}

    // getNextProcessableOp uses the [status+priority] compound index (v17) so
    // IndexedDB returns records pre-sorted by priority, and skips embedding ops
    // when the backend is in backoff (keeping them pending for later retry).
    //
    // embeddingProcessedThisRun: once true, getNextProcessableOp is called with
    // skipEmbedding=true so subsequent iterations process non-embedding ops only.
    // This limits each processQueue run to ONE embedding batch (~50 URLs ≈ 2–3 min),
    // preventing Chrome's service-worker watchdog from killing us mid-run when
    // there are large queues (e.g. 392 pending ops → 8 batches ≈ 16 minutes).
    // A re-schedule at the end of the loop picks up the remaining embedding ops.
    let pendingOp = await getNextProcessableOp();
    let opsProcessedThisRun = 0;
    let embeddingProcessedThisRun = false;

    while (pendingOp) {
      await processOperation(pendingOp);
      opsProcessedThisRun++;

      if (pendingOp.type === 'generateEmbedding') {
        embeddingProcessedThisRun = true;
      }

      const currentMemory = typeof performance !== 'undefined' ? (performance as PerformanceWithMemory).memory?.usedJSHeapSize : undefined;
      if (currentMemory && currentMemory > 200 * 1024 * 1024) {
          logger.warn(`[QueueWorker] High memory usage detected: ${Math.round(currentMemory/1024/1024)}mb`);
      }

      // After one embedding batch, continue with non-embedding ops only.
      pendingOp = await getNextProcessableOp(embeddingProcessedThisRun);
    }

    // Log a summary so we can see whether any pending ops were left behind in this run.
    // "Left behind" means they were created during this run (after the while loop's final check)
    // and will need a new processQueue trigger (from enqueueOperation's sendMessage) to be processed.
    try {
      const remainingPending = await db.operations.where('status').equals('pending').toArray();
      if (remainingPending.length > 0) {
        const byType: Record<string, number> = {};
        for (const op of remainingPending) { byType[op.type] = (byType[op.type] || 0) + 1; }
        logger.info(
          `[QueueWorker] processQueue done: processed ${opsProcessedThisRun} op(s). ` +
          `${remainingPending.length} pending op(s) remain (will need separate trigger): ${JSON.stringify(byType)}`,
        );
      } else {
        logger.info(`[QueueWorker] processQueue done: processed ${opsProcessedThisRun} op(s). Queue is empty.`);
      }
    } catch (_) {}

    // If we processed one embedding batch and there are still more pending (without
    // backoff), schedule a follow-up processQueue in 2 seconds. The pending timer
    // keeps the service worker alive while allowing Chrome to GC before the next
    // WASM model load. If the SW is killed in those 2s, the ops will be picked up
    // on the next restart via initQueueProcessor's startup processQueue call.
    if (embeddingProcessedThisRun && !isEmbeddingInBackoff()) {
      try {
        const remainingEmbOps = await db.operations
          .where('[status+priority]')
          .between(['pending', Dexie.minKey], ['pending', Dexie.maxKey])
          .filter(op => op.type === 'generateEmbedding')
          .count();
        if (remainingEmbOps > 0) {
          logger.info(
            `[QueueWorker] ${remainingEmbOps} embedding op(s) remain after this batch; ` +
            `scheduling next batch in 2s.`
          );
          setTimeout(() => processQueue(), 2000);
        }
      } catch (_) {}
    }

    // If embedding ops are still pending due to backoff, schedule a single retry
    // for when the backoff window expires so they aren't permanently deferred.
    await maybeScheduleEmbeddingRetry();
  } catch (e: unknown) {
    logger.error('[QueueWorker] Error processing queue:', e);
  } finally {
    isProcessing = false;
    const endMemory = typeof performance !== 'undefined' ? (performance as PerformanceWithMemory).memory?.usedJSHeapSize : undefined;
    logger.info('[QueueWorker] Finished processing queue. Memory diff:', (startMemory != null && endMemory != null) ? endMemory - startMemory : 'unknown');
  }
}

import { reportProgress, clearProgress } from '../db/progress';

async function processOperation(op: Operation) {
    if (!op.id) return;

    // Update status to processing
    await db.operations.update(op.id, { status: 'processing' });
    const progressId = `op-${op.id}`;
    logger.info(`[QueueWorker] Starting operation: ${op.type} (ID: ${op.id})`, op.payload);

    let isCompleted = false;
    try {
        switch (op.type) {
            case 'renormalize':
                await handleRenormalize(op.payload, progressId);
                break;
            case 'cleanup':
                await handleCleanup(op.payload);
                break;
            case 'stash':
                await handleStash(op.payload);
                break;
            case 'generateEmbedding':
                await handleGenerateEmbedding(op.payload, progressId);
                break;
            case 'fetchMissingTitle':
                await handleFetchMissingTitle(op.payload);
                break;
            case 'cleanupGraphRefs':
                await handleCleanupGraphRefs(op.payload, progressId);
                break;
            default:
                throw new Error(`Unknown operation type: ${op.type}`);
        }
        logger.info(`[QueueWorker] Completed operation ${op.id} successfully.`);
        await db.operations.update(op.id, { status: 'completed' });
        isCompleted = true;
    } catch (e: unknown) {
        logger.error(`[QueueWorker] Operation ${op.id} failed:`, e);
        await db.operations.update(op.id, {
            status: 'failed',
            error: e instanceof Error ? e.message : String(e),
        });
    } finally {
        await clearProgress(progressId, isCompleted);
        // After each embedding op (success or failure), close the offscreen document
        // to free the WASM heap. The WASM memory grows monotonically during inference
        // and never shrinks within a document lifetime. Recycling between batches
        // prevents OOM when processing large queues (hundreds of pending ops).
        // The next embedding op will recreate the document via embeddingProxy.init().
        if (op.type === 'generateEmbedding') {
            try {
                await embeddingProxy.closeDocument();
            } catch (e: unknown) {
                logger.warn('[QueueWorker] Failed to close offscreen document after embedding op:', e);
            }
        }
    }
}

// Handler implementations

async function handleRenormalize(payload: { limit?: number; batchSize?: number }, progressId: string) {
    // renormalizeAll takes optional limit. Payload usually empty or has limit.
    const limit = payload?.limit;
    const batchSize = payload?.batchSize || 100;

    // Report initial progress immediately so UI shows something
    await reportProgress(progressId, 'background', 'renormalize', 0, 100);

    await renormalizeAll(limit, batchSize, (current, total) => {
        reportProgress(progressId, 'background', 'renormalize', current, total).catch(
            err => logger.error('[QueueWorker] Failed to report renormalize progress:', err)
        );
    });
}

// Helper to get open tabs without redux
async function getOpenTabs(retainWindowId?: number) {
  const windows = await chrome.windows.getAll({ populate: true });
  const tabs: chrome.tabs.Tab[] = [];
  for (const window of windows) {
    if (window.id === retainWindowId || window.id === undefined) continue;
    if (window.tabs) {
      tabs.push(...window.tabs);
    }
  }
  return tabs;
}

async function handleCleanup(payload: { retainWindowId?: number }) {
    const { retainWindowId } = payload;
    const tabs = await getOpenTabs(retainWindowId);

    // Identify tabs with no tags (candidates for stashing)
    // We must normalize urls first
    const items = tabs.map(t => {
        if (!t.url) return null;
        try {
            return normalizeUrl(t.url);
        } catch (e) {
            return null;
        }
    }).filter(Boolean) as NormalizedUrl[];

    const pagesToStash = await fetchPagesWithNoTags(items);
    const urlsToStash = pagesToStash.map((page) => page.nurl.normalized);
    if (urlsToStash.length > 0) {
        await addTag(urlsToStash, StashTag);
    }

    const protectTags = getTagsToProtectFromCleanup();
    // Intentional: only 'Main'-tagged pages are protected from cleanup. #flag, #stash, etc. are
    // all closed — cleanup is a "close everything except your curated Main pages" operation.
    // Re-fetch pages to check for protected tags
    const allTabUrls = items.map(n => n.normalized);
    const pages: Page[] = await fetchPages(allTabUrls);
    
    const protectUrls = pages
      .filter((p) => p.humanTags && p.humanTags.some((t: string) => protectTags.includes(t)))
      .map((p) => p.nurl.normalized);

    for (const tab of tabs) {
      if (!tab.url) continue;
      const normalized = normalizeUrl(tab.url).normalized;
      // Close if not protected
      // logic in windowsSlice was: if (tab.url && !protectUrls.includes(tab.url.normalized))
      if (!protectUrls.includes(normalized)) {
        if(tab.id) {
           try {
               await closeTab(tab.id);
           } catch(e: any) {
               logger.warn(`[QueueWorker] Failed to close tab ${tab.id} during cleanup:`, e);
           }
        }
      }
    }
}

async function handleStash(payload: { retainWindowId?: number }) {
    const { retainWindowId } = payload;
    const tabs = await getOpenTabs(retainWindowId);

    const urls = tabs.map(t => t.url).filter(u => u !== undefined) as string[];

    if (urls.length > 0) {
        await addTag(urls, StashTag);
    }
    
    // Close tabs
    for (const tab of tabs) {
        if (tab.id) {
            try {
                await closeTab(tab.id);
            } catch (e: any) {
                logger.warn(`[QueueWorker] Failed to close tab ${tab.id} during stash:`, e);
            }
        }
    }
}

async function handleFetchMissingTitle(payload: { url?: string; limit?: number }, progressId?: string) {
    if (!payload.url) {
        // Bulk mode: find all pages without titles and fetch them sequentially.
        // Skip todo:// and other non-http(s) URLs — they can't be opened in a tab.
        // Also skip pages where a previous fetch attempt already failed (titleFetchFailed),
        // to avoid re-opening background tabs on every restart for unfetchable URLs.
        // Keep the bulk limit very small: each title fetch opens a real browser tab that
        // loads the full page, consuming CPU and memory. A limit of 5 means at most 5 tabs
        // are opened per queue run (≤50 s at 10 s/timeout) vs. the old limit of 50 (≤500 s).
        // Reducing this also prevents the queue processor from being blocked long enough for
        // Chrome to kill the service worker, which would reset the op and re-open the tabs.
        const BULK_LIMIT = payload.limit ?? 5;
        const urlsToProcess = (await db.pages
            .filter(p => (!p.title || p.title === '') && !p.titleFetchFailed && (p.url.startsWith('http://') || p.url.startsWith('https://')))
            .primaryKeys()) as string[];
        const capped = urlsToProcess.slice(0, BULK_LIMIT);
        logger.info(`[QueueWorker] Bulk fetchMissingTitle: ${urlsToProcess.length} pages need titles, processing up to ${BULK_LIMIT}`);
        for (const u of capped) {
            await handleFetchMissingTitle({ url: u });
        }
        return;
    }

    const { url } = payload;
    // Only fetch titles for web URLs — todo:// and other schemes can't be opened in a tab.
    if (!url.startsWith('http://') && !url.startsWith('https://')) {
        return;
    }
    const page = await db.pages.get(url);
    if (!page || (page.title && page.title !== '')) {
        return;
    }

    try {
        // Direct fetch from service worker frequently fails due to CORS in MV3.
        // The most reliable way to get a page's title without CORS issues is to
        // open it in a background tab, wait for it to load, grab the title, and close it.
        const tab = await chrome.tabs.create({ url, active: false });
        
        if (tab && tab.id) {
            // We use a Promise that resolves when the tab finishes loading
            const fetchPromise = new Promise<string>((resolve, reject) => {
                const listener = (tabId: number, info: chrome.tabs.TabChangeInfo, updatedTab: chrome.tabs.Tab) => {
                    if (tabId === tab.id && info.status === 'complete') {
                        chrome.tabs.onUpdated.removeListener(listener);
                        clearTimeout(timeoutId);
                        resolve(updatedTab.title || '');
                    }
                };
                chrome.tabs.onUpdated.addListener(listener);

                // Fallback timeout in case the page never fully "completes"
                const timeoutId = setTimeout(() => {
                    chrome.tabs.onUpdated.removeListener(listener);
                    if (tab.id) {
                        chrome.tabs.get(tab.id).then(t => resolve(t.title || '')).catch(() => reject(new Error('Timeout')));
                    } else {
                        reject(new Error('Timeout'));
                    }
                }, 10000); // 10 second timeout
            });
            
            try {
                const title = await fetchPromise;
                if (title && title !== url) {
                    await db.pages.update(url, { title });
                    logger.info(`[QueueWorker] Fetched missing title via background tab for ${url}: ${title}`);
                } else {
                    // No usable title obtained (timeout, empty, or URL-as-title).
                    // Mark the page so the bulk sweep skips it on future startups and
                    // does not keep opening background tabs for unfetchable URLs.
                    await db.pages.update(url, { titleFetchFailed: true });
                    logger.warn(`[QueueWorker] Could not obtain title for ${url}; marking titleFetchFailed`);
                }
            } finally {
                // Always close the temporary tab
                await chrome.tabs.remove(tab.id).catch(e => logger.warn('Failed to close temp fetch tab:', e));
            }
        }
    } catch (e: any) {
        logger.warn(`[QueueWorker] Failed to fetch missing title for ${url}:`, e);
        // Mark the page so we don't retry on every restart.
        await db.pages.update(url, { titleFetchFailed: true }).catch(() => {});
    }
}
async function handleCleanupGraphRefs(payload: { url?: string }, progressId: string) {
    if (payload.url) {
        const { url } = payload;
        logger.info(`[QueueWorker] cleanupGraphRefs (single): removing refs to ${url}`);

        // Read all pages in batches to find those referencing the deleted URL.
        // The old approach (filter().modify() over all pages) held an exclusive write
        // lock on db.pages for the entire scan — tens of seconds for large DBs — which
        // blocked backup reads and similarity-worker reads. This two-pass approach uses
        // multiple short read transactions followed by targeted writes.
        const allUrls = (await db.pages.toCollection().primaryKeys()) as string[];
        const matchingUrls: string[] = [];
        const READ_BATCH = 500;
        for (let i = 0; i < allUrls.length; i += READ_BATCH) {
            const batch = allUrls.slice(i, i + READ_BATCH);
            const pages = await db.pages.bulkGet(batch);
            for (const page of pages) {
                if (!page) continue;
                if (
                    page.openerUrls?.includes(url) ||
                    page.openedUrls?.includes(url) ||
                    page.timeAdjacentUrls?.includes(url)
                ) {
                    matchingUrls.push(page.url);
                }
            }
        }

        if (matchingUrls.length === 0) {
            logger.info(`[QueueWorker] cleanupGraphRefs (single): no pages reference ${url}`);
            return;
        }

        logger.info(`[QueueWorker] cleanupGraphRefs (single): updating ${matchingUrls.length} page(s)`);
        const WRITE_BATCH = 100;
        for (let i = 0; i < matchingUrls.length; i += WRITE_BATCH) {
            const batch = matchingUrls.slice(i, i + WRITE_BATCH);
            await db.pages.where(':id').anyOf(batch).modify(p => {
                if (p.openerUrls?.includes(url))      p.openerUrls      = p.openerUrls.filter(u => u !== url);
                if (p.openedUrls?.includes(url))       p.openedUrls       = p.openedUrls.filter(u => u !== url);
                if (p.timeAdjacentUrls?.includes(url)) p.timeAdjacentUrls = p.timeAdjacentUrls.filter(u => u !== url);
            });
        }
    } else {
        // Bulk sweep mode: strip all dead URLs from every page's graph arrays,
        // then remove orphaned tags from the tags table.
        logger.info('[QueueWorker] cleanupGraphRefs (bulk): starting full graph sweep');

        // Step 1: snapshot all valid URLs. Pages deleted after this point may leave a
        // one-generation dangle that gets caught on the next Renormalize run — acceptable.
        const validUrls = new Set(
            (await db.pages.toCollection().primaryKeys()) as string[]
        );
        const allPageKeys = [...validUrls];
        const total = allPageKeys.length;
        let processed = 0;
        await reportProgress(progressId, 'background', 'cleanupGraphRefs', processed, total);

        // Step 2: iterate all pages in batches, filter graph arrays against validUrls.
        const BATCH_SIZE = 500;
        for (let i = 0; i < allPageKeys.length; i += BATCH_SIZE) {
            const batch = allPageKeys.slice(i, i + BATCH_SIZE);
            await db.pages.where(':id').anyOf(batch).modify(p => {
                if (p.openerUrls?.length)
                    p.openerUrls = p.openerUrls.filter(u => validUrls.has(u));
                if (p.openedUrls?.length)
                    p.openedUrls = p.openedUrls.filter(u => validUrls.has(u));
                if (p.timeAdjacentUrls?.length)
                    p.timeAdjacentUrls = p.timeAdjacentUrls.filter(u => validUrls.has(u));
            });
            processed += batch.length;
            await reportProgress(progressId, 'background', 'cleanupGraphRefs', processed, total);
        }

        // Step 3: remove orphaned tags — tags in the tags table that no page references.
        const allTagNames = (await db.tags.toCollection().primaryKeys()) as string[];
        const orphaned: string[] = [];
        for (const tag of allTagNames) {
            const count = await db.pages.where('humanTags').equals(tag).count();
            if (count === 0) orphaned.push(tag);
        }
        if (orphaned.length > 0) {
            logger.info(`[QueueWorker] cleanupGraphRefs: removing ${orphaned.length} orphaned tags:`, orphaned);
            // Chunk to prevent "Maximum call stack size exceeded" in Dexie's internal query expansion
            const TAG_BATCH_SIZE = 500;
            for (let i = 0; i < orphaned.length; i += TAG_BATCH_SIZE) {
                await db.tags.bulkDelete(orphaned.slice(i, i + TAG_BATCH_SIZE));
            }
        }

        logger.info(`[QueueWorker] cleanupGraphRefs (bulk): done. Processed ${total} pages, removed ${orphaned.length} orphaned tags.`);
    }
}

// Import embedding proxy to run in offscreen document
import { embeddingProxy } from '../services/embeddingProxy';

// Exponential-backoff state for embedding backend failures.
// A transient WASM error sets a retry delay; after it expires the backend is retried.
// Resets on success so repeated failures keep backing off from the last success.
const EMBEDDING_INITIAL_BACKOFF_MS = 30_000;       // 30 seconds
const EMBEDDING_MAX_BACKOFF_MS = 30 * 60_000;      // 30 minutes
const EMBEDDING_BACKOFF_MULTIPLIER = 2;

interface EmbeddingBackoffState {
  failureCount: number;
  nextRetryAt: number; // epoch ms; 0 = no backoff active
}

let embeddingBackoff: EmbeddingBackoffState = { failureCount: 0, nextRetryAt: 0 };

/** Called on every successful embedding generation to reset the backoff counter. */
function onEmbeddingSuccess(): void {
  embeddingBackoff = { failureCount: 0, nextRetryAt: 0 };
  if (embeddingRetryTimer !== null) {
    clearTimeout(embeddingRetryTimer);
    embeddingRetryTimer = null;
  }
}

/** Called when the backend reports a fatal initialisation failure. */
function onEmbeddingBackendFailure(): void {
  embeddingBackoff.failureCount++;
  const delayMs = Math.min(
    EMBEDDING_INITIAL_BACKOFF_MS * Math.pow(EMBEDDING_BACKOFF_MULTIPLIER, embeddingBackoff.failureCount - 1),
    EMBEDDING_MAX_BACKOFF_MS,
  );
  embeddingBackoff.nextRetryAt = Date.now() + delayMs;
  logger.warn(
    `[QueueWorker] Embedding backend failed (attempt ${embeddingBackoff.failureCount}). ` +
    `Retrying in ${Math.round(delayMs / 1000)}s.`,
  );
}

/** Returns true when the backoff window has not yet expired. */
function isEmbeddingInBackoff(): boolean {
  return embeddingBackoff.nextRetryAt > Date.now();
}

/**
 * Exposed for tests only — resets backoff state so tests are independent.
 * @internal
 */
export function resetEmbeddingBackoff(): void {
  embeddingBackoff = { failureCount: 0, nextRetryAt: 0 };
  if (embeddingRetryTimer !== null) {
    clearTimeout(embeddingRetryTimer);
    embeddingRetryTimer = null;
  }
}

/**
 * Returns the highest-priority pending op that can be processed right now.
 * Embedding ops are excluded when:
 *   - the embedding backend is in backoff (they stay pending for maybeScheduleEmbeddingRetry), or
 *   - skipEmbedding is true (caller has already processed one embedding batch this run).
 *
 * The skipEmbedding flag limits each processQueue run to ONE embedding batch (~50 URLs).
 * This bounds the run to 2–3 minutes instead of potentially 16+ minutes for large queues,
 * preventing Chrome from killing the service worker mid-batch. Non-embedding ops (cleanup,
 * stash, etc.) are still processed in the same run after the embedding batch completes.
 */
async function getNextProcessableOp(skipEmbedding = false): Promise<Operation | undefined> {
  const ops = await db.operations
    .where('[status+priority]')
    .between(['pending', Dexie.minKey], ['pending', Dexie.maxKey])
    .toArray();
  return ops.find(op => {
    if (op.type === 'generateEmbedding' && isEmbeddingInBackoff()) return false;
    if (op.type === 'generateEmbedding' && skipEmbedding) return false;
    return true;
  });
}

/**
 * If there are pending embedding ops and the backend is in backoff, schedule
 * a single processQueue retry for when the backoff window expires.
 * Idempotent — does nothing if a timer is already pending or backoff is over.
 */
async function maybeScheduleEmbeddingRetry(): Promise<void> {
  if (!isEmbeddingInBackoff() || embeddingRetryTimer !== null) return;
  const remaining = Math.max(embeddingBackoff.nextRetryAt - Date.now(), 0);
  if (remaining <= 0) return;

  const pendingEmbOps = await db.operations
    .where('[status+priority]')
    .between(['pending', Dexie.minKey], ['pending', Dexie.maxKey])
    .filter(op => op.type === 'generateEmbedding')
    .count();

  if (pendingEmbOps > 0) {
    logger.info(
      `[QueueWorker] ${pendingEmbOps} embedding op(s) deferred; ` +
      `backend in backoff for ${Math.ceil(remaining / 1000)}s. ` +
      `Scheduling retry after backoff expires.`
    );
    embeddingRetryTimer = setTimeout(() => {
      embeddingRetryTimer = null;
      processQueue();
    }, remaining + 1000);
  }
}

// Timer handle for scheduling a processQueue retry after the embedding backoff expires.
// Kept as module-level state so we never schedule more than one retry timer at a time.
let embeddingRetryTimer: ReturnType<typeof setTimeout> | null = null;

// Abort controller for the currently-running embedding batch. Replaced each time a new
// batch starts so that a previous long-running batch is cancelled immediately.
let embeddingBatchAbortController: AbortController | null = null;

// Number of texts sent to the WASM model in each batched inference call.
// Larger batches improve throughput (vectorised matrix ops) at the cost of peak memory.
// 16 is a good balance for all-MiniLM-L6-v2 with typical tab title / URL lengths.
const EMBEDDING_BATCH_SIZE = 16;

// Maximum URLs processed in a single handleGenerateEmbedding call.
// Chrome MV3 service workers can be killed after ~5 minutes of continuous execution.
// Bounding the per-run work to ≤50 URLs (3–4 ONNX batches ≈ 60–120 s including model
// load) ensures the run completes before Chrome's watchdog fires, so each call makes
// real forward progress rather than resetting endlessly. Bulk runs re-enqueue themselves
// if more pages remain; URL-specific coalescing stops absorbing at this threshold.
const MAX_URLS_PER_EMBEDDING_RUN = 50;

async function handleGenerateEmbedding(payload: { url?: string; urls?: string[]; limit?: number }, progressId: string) {
    if (isEmbeddingInBackoff()) {
        // processQueue normally prevents this path by skipping embedding ops via
        // getNextProcessableOp. If reached anyway (e.g. direct call in tests), complete
        // without work and log a warning so the caller can be fixed.
        const remaining = Math.ceil((embeddingBackoff.nextRetryAt - Date.now()) / 1000);
        logger.warn(
            `[QueueWorker] handleGenerateEmbedding called during backoff ` +
            `(${remaining}s remaining; failure count: ${embeddingBackoff.failureCount}). ` +
            `Completing without generating embeddings — caller should check isEmbeddingInBackoff().`,
        );
        return;
    }

    // Abort any in-flight embedding batch and start a fresh controller for this one.
    embeddingBatchAbortController?.abort();
    const abortController = new AbortController();
    embeddingBatchAbortController = abortController;
    const { signal } = abortController;

    await reportProgress(progressId, 'background', 'generateEmbedding', 0, 100);

    // Determine the URL list for this operation.
    // null = bulk mode (process all pages without embeddings).
    let urlsToProcess: string[] | null = null;
    if (payload.url) {
        urlsToProcess = [payload.url];
    } else if (payload.urls) {
        urlsToProcess = [...payload.urls];
    }

    // Coalesce other pending generateEmbedding operations into this one.
    // This eliminates the per-operation DB round-trips (status → processing → completed)
    // that accumulate when many individual tag changes each enqueue their own op.
    // Only applicable in URL-specific mode; bulk mode already covers everything.
    //
    // Coalescing is capped at MAX_URLS_PER_EMBEDDING_RUN so that a single processQueue
    // call never processes more than that many URLs. This bounds the wall-clock time of
    // one call to roughly 60–120 s (3–4 ONNX batches + model load), well within Chrome's
    // MV3 service-worker lifetime. Ops beyond the cap remain pending and are picked up in
    // the next processQueue run, ensuring forward progress even if the SW is killed.
    const absorbedOpIds: number[] = [];
    if (urlsToProcess !== null) {
        const pendingEmbeddingOps = await db.operations
            .where('[status+priority]')
            .between(['pending', Dexie.minKey], ['pending', Dexie.maxKey])
            .filter(op => op.type === 'generateEmbedding')
            .toArray();

        for (const op of pendingEmbeddingOps) {
            if (op.id === undefined) continue;
            const p = op.payload as { url?: string; urls?: string[] };
            if (!p.url && !p.urls) {
                // A pending bulk op exists — absorb it and switch to bulk mode so we
                // cover all pages rather than just the union of the listed URLs.
                urlsToProcess = null;
                absorbedOpIds.push(op.id);
                break;
            }
            // Stop absorbing if we've already reached the per-run URL cap.
            // The remaining ops stay pending and will be processed in the next run.
            const incomingCount = p.url ? 1 : (p.urls?.length ?? 0);
            if (urlsToProcess.length + incomingCount > MAX_URLS_PER_EMBEDDING_RUN) {
                break;
            }
            if (p.url) urlsToProcess.push(p.url);
            else if (p.urls) urlsToProcess.push(...p.urls);
            absorbedOpIds.push(op.id);
        }

        if (absorbedOpIds.length > 0) {
            logger.info(`[QueueWorker] Coalescing ${absorbedOpIds.length} pending embedding op(s) into this batch`);
        }

        // Deduplicate: multiple coalesced ops can contain overlapping URLs (e.g. two
        // addTag calls on overlapping page sets). De-duping avoids redundant DB reads
        // and embedding inference for the same URL within a single batch run.
        if (urlsToProcess !== null) {
            urlsToProcess = [...new Set(urlsToProcess)];
        }
    }

    // Log coalescing summary so we can see how many ops were merged vs left pending.
    if (urlsToProcess !== null) {
        logger.info(
            `[QueueWorker] handleGenerateEmbedding: processing ${urlsToProcess.length} URL(s), ` +
            `absorbed ${absorbedOpIds.length} additional pending op(s) into this batch`,
        );
    } else {
        logger.info(
            `[QueueWorker] handleGenerateEmbedding: bulk mode (absorbed ${absorbedOpIds.length} pending op(s))`,
        );
    }

    // Initialise the embedding backend once before any processing.
    try {
        logger.info('[QueueWorker] Initialising embedding backend (offscreen WASM)...');
        await embeddingProxy.init();
        logger.info('[QueueWorker] Embedding backend initialised successfully.');
    } catch (e: unknown) {
        const msg = e instanceof Error ? e.message : String(e);
        if (e instanceof Error && e.message.includes('no available backend found')) {
            logger.error('[QueueWorker] Backend failed to initialise (no available backend). Applying exponential backoff.');
            onEmbeddingBackendFailure();
        } else {
            logger.error(`[QueueWorker] Embedding backend init failed with unexpected error: ${msg}`, e);
        }
        throw e; // Always propagate — processOperation marks the op as failed
    }

    if (urlsToProcess !== null) {
        logger.info(`[Embeddings] Generating for ${urlsToProcess.length} URL(s) (URL-specific mode)`);
        await processEmbeddingBatch(urlsToProcess, signal, progressId, payload.limit);
    } else {
        logger.info('[Embeddings] Starting bulk generation for all pages without embeddings...');
        const allUrls = (await db.pages.filter(p => !p.embedding && !p.embeddingErrored).primaryKeys()) as string[];
        logger.info(`[Embeddings] Found ${allUrls.length} pages without embeddings.`);
        // Cap per run so the SW doesn't get killed mid-batch. If there are more pages,
        // re-enqueue a fresh bulk op — it will be picked up by the next processQueue run.
        const cap = payload.limit ?? MAX_URLS_PER_EMBEDDING_RUN;
        const urlsForThisRun = allUrls.slice(0, cap);
        await processEmbeddingBatch(urlsForThisRun, signal, progressId, undefined);
        if (!payload.limit && allUrls.length > cap) {
            logger.info(`[Embeddings] ${allUrls.length - cap} pages remain; re-enqueueing bulk op for next run.`);
            await enqueueOperation('generateEmbedding', {}, 10);
        }
    }

    // Mark coalesced ops as completed now that their URLs have been processed.
    // If processEmbeddingBatch threw, we skip this and leave them as pending for retry.
    if (absorbedOpIds.length > 0) {
        const MARK_BATCH = 100;
        for (let i = 0; i < absorbedOpIds.length; i += MARK_BATCH) {
            await db.operations
                .where('id')
                .anyOf(absorbedOpIds.slice(i, i + MARK_BATCH))
                .modify({ status: 'completed' });
        }
    }
}

/**
 * Process a list of URLs: fetch pages, generate embeddings in batches via a single
 * ONNX inference call per batch, and write results in one DB transaction per batch.
 *
 * Using batch inference instead of N individual calls is the primary speed improvement:
 * the WASM runtime can vectorise matrix multiplications across the batch dimension,
 * making throughput roughly proportional to batch size rather than number of round-trips.
 */
async function processEmbeddingBatch(
    urls: string[],
    signal: AbortSignal,
    progressId: string,
    limit?: number,
): Promise<void> {
    const total = urls.length;
    let processed = 0;
    let embeddedCount = 0;
    let skippedCount = 0;
    let erroredCount = 0;
    await reportProgress(progressId, 'background', 'generateEmbedding', processed, total);

    for (let i = 0; i < urls.length; i += EMBEDDING_BATCH_SIZE) {
        if (signal.aborted) {
            logger.info(`[QueueWorker] Embedding batch aborted at ${processed}/${total}`);
            break;
        }
        if (limit !== undefined && processed >= limit) break;

        const batchUrls = urls.slice(i, i + EMBEDDING_BATCH_SIZE);
        const rawPages = await db.pages.bulkGet(batchUrls);

        // Split: pages needing embedding vs. pages with empty text (mark errored immediately)
        const toEmbed: { page: DBPage; text: string }[] = [];
        const toError: DBPage[] = [];
        let batchSkipped = 0;
        for (const page of rawPages) {
            if (!page || page.embedding || page.embeddingErrored) {
                batchSkipped++;
                continue;
            }
            const semanticTags = page.humanTags.filter(t => !SystemTags.includes(t));
            const text = `${page.title || ''} ${page.url} ${semanticTags.join(' ')}`.trim();
            if (!text) {
                toError.push(page);
            } else {
                toEmbed.push({ page, text });
            }
        }
        skippedCount += batchSkipped;
        if (batchSkipped > 0) {
            logger.info(`[Embeddings] Batch ${Math.floor(i / EMBEDDING_BATCH_SIZE) + 1}: ${batchSkipped}/${batchUrls.length} page(s) skipped (already have embedding or errored)`);
        }

        if (toError.length > 0) {
            erroredCount += toError.length;
            await db.transaction('rw', db.pages, async () => {
                for (const p of toError) {
                    await db.pages.update(p.url, { embeddingErrored: true });
                }
            });
        }

        if (toEmbed.length > 0) {
            try {
                logger.debug(`[Embeddings] Batch inference: ${toEmbed.length} text(s)`);
                const texts = toEmbed.map(x => x.text);
                const embeddings = await embeddingProxy.generateEmbeddings(texts, signal);

                // Write all results in one transaction to minimise DB round-trips.
                await db.transaction('rw', db.pages, async () => {
                    for (let j = 0; j < toEmbed.length; j++) {
                        await db.pages.update(toEmbed[j].page.url, {
                            embedding: embeddings[j],
                            embeddingErrored: false,
                        });
                    }
                });

                embeddedCount += toEmbed.length;
                onEmbeddingSuccess();
            } catch (e: unknown) {
                if (e instanceof Error && e.name === 'AbortError') {
                    logger.info('[QueueWorker] Embedding batch aborted mid-chunk');
                    break;
                }
                const errMsg = e instanceof Error ? e.message : String(e);
                const errStack = e instanceof Error && e.stack ? `\n${e.stack}` : '';
                logger.error(`[Embeddings] Batch embedding failed: ${errMsg}${errStack}`);

                const isTimeout = e instanceof Error && e.message === 'Embedding message timeout';
                if (isTimeout) {
                    // A message timeout is transient — the offscreen document may have been
                    // slow or unresponsive this run but could succeed next time. Propagate the
                    // error so processOperation marks the op as failed; the remaining pending
                    // ops (not yet absorbed into this batch) will be retried on the next run.
                    // Do NOT mark pages as permanently errored.
                    throw e;
                }

                // For non-timeout failures, mark the chunk as permanently errored so future
                // runs don't retry indefinitely (avoids hammering a broken URL forever).
                await db.transaction('rw', db.pages, async () => {
                    for (const { page } of toEmbed) {
                        await db.pages.update(page.url, { embeddingErrored: true });
                    }
                });

                if (e instanceof Error && e.message.includes('no available backend found')) {
                    onEmbeddingBackendFailure();
                    throw e;
                }
            }
        }

        processed += batchUrls.length;
        await reportProgress(progressId, 'background', 'generateEmbedding', processed, total);
    }

    logger.info(
        `[Embeddings] processEmbeddingBatch done: ${embeddedCount} embedded, ` +
        `${skippedCount} skipped (already embedded/errored), ${erroredCount} newly errored (no text). ` +
        `Total URLs in payload: ${total}.`,
    );
}
